import React, { useState, useEffect, useRef } from 'react';
import iziToast from 'izitoast';
import {
  Heading,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Button,
  useToast,
  Modal,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  ModalOverlay,
  Divider
} from '@chakra-ui/react';
import 'moment/locale/fi';

import BackendApi from './api/BackendApi';
import moment from 'moment';
import MapPicker from './components/GoogleMapsPicker';
import { AttachmentIcon } from '@chakra-ui/icons';

interface NewTicketFormProps {
  customerData: Record<string, string>;
  callbackFormIsDone(): void;
  fetchTickets: () => Promise<void>;
}

export const NewTicketForm = (props: NewTicketFormProps) => {
  const [formData, setFormData] = useState({
    firstName: props.customerData.firstName || '',
    lastName: props.customerData.lastName || '',
    email: props.customerData.email || '',
    phone: props.customerData.phone || '',
    target: '',
    content: '',
    date: moment().format('YYYY-MM-DDTHH:mm'),
    location: ''
  });
  const [files, setFiles] = useState<FileList>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const toast = useToast();

  const handleFileInputReset = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = ''; // Reset the file input
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData, // Keep the other form data
      [name]: value // Update the specific field
    });
  };

  useEffect(() => {
    if (isSubmitting) {
      const query = window.location.search;
      const urlParams = new URLSearchParams(query);
      const hash = urlParams.get('hash');
      const data = new FormData();

      if (files) {
        for (var i = 0; i < files?.length; i++) {
          const file = files[i];
          data.append('files[' + i + ']', file);
        }
      }

      formData.content =
        `
        Kuljettaja: ${formData.target}\n
        Päivämäärä: ${moment(formData.date).format('DD.MM.YYYY [klo] HH:mm')}\n
        Paikka: ${
          (formData.location && formData.location.split(',').map((x) => parseInt(x, 10).toFixed(5))) || 'Ei valittu'
        }\n
        Etunimi: ${formData.firstName}\n
        Sukunimi: ${formData.lastName}\n
        Sähköposti: ${formData.email}\n
        Puhelinnumero: ${formData.phone}\n
        ${
          files?.length > 0
            ? `Liitteet: ${Object.keys(files || [])
                .map((x) => files[x].name)
                .join(', ')}\n`
            : ''
        }
        \n\n
      ` + formData.content;

      Object.keys(formData).map((key) => {
        data.append(key, formData[key]);
      });

      BackendApi.createNewTicket(hash, data)
        .then((response) => {
          toast({
            title: 'Viesti lähetetty.',
            description: 'Vastaamme 2-3 arkipäivän kuluessa',
            status: 'success',
            duration: 5000,
            isClosable: true
          });
          if (response.status === 200 || response.status === 201) {
            iziToast.success({
              title: 'Onnistui',
              message: 'Uusi palvelupyyntö luotu'
            });
            props.fetchTickets();
          }
        })
        .catch((error) => {
          console.error(error);
          toast({
            title: 'Virhe',
            description: 'Viestin tallentaminen epäonnistui.',
            status: 'error',
            duration: 3000,
            isClosable: true
          });
        })
        .finally(() => {
          setIsSubmitting(false);
          props.callbackFormIsDone();
        });
    }
  }, [isSubmitting]);

  const onPreview = () => {
    setIsPreviewing(true);
  };

  const onSubmit = () => {
    setIsSubmitting(true);
  };

  const isSendingDisabled = () => {
    // check, something is missing
    return !Object.keys(formData)
      .filter((k) => ['firstName', 'lastName', 'email', 'phone', 'content', 'target'].includes(k))
      .every((key) => formData[key] !== '');
  };

  const getPreview = () => {
    return (
      <div>
        <Heading as="h4" size="md">
          Kuljetus
        </Heading>
        <div>Kuljettaja: {formData.target}</div>
        <div>Päivämäärä: {moment(formData.date).format('DD.MM.YYYY [klo] HH:mm')}</div>
        <div>
          Paikka:{' '}
          {formData.location
            ?.split(',')
            .map((x) => parseFloat(x).toFixed(5))
            .join(', ') || 'Ei valittu'}
        </div>
        <Heading as="h4" size="md">
          Yhteystietosi
        </Heading>
        <div>Etunimi: {formData.firstName}</div>
        <div>Sukunimi: {formData.lastName}</div>
        <div>Sähköposti: {formData.email}</div>
        <div>Puhelinnumero: {formData.phone}</div>
        <Heading as="h4" size="md">
          Viesti
        </Heading>
        {Object.keys(files || []).map((x) => {
          return (
            <div>
              <AttachmentIcon /> {files[x].name}
            </div>
          );
        })}
        <Divider style={{ margin: '5px 0' }} />
        <div>{formData.content}</div>
      </div>
    );
  };

  return (
    <div className="portalNewTicketForm">
      <Modal isOpen={isPreviewing} onClose={() => setIsPreviewing(false)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Yhteenveto</ModalHeader>
          <ModalCloseButton />
          <ModalBody>{getPreview()}</ModalBody>

          <ModalFooter>
            <Button
              colorScheme="blue"
              mr={3}
              onClick={() => {
                setIsPreviewing(false);
                onSubmit();
              }}
            >
              Lähetä
            </Button>
            <Button variant="ghost" onClick={() => setIsPreviewing(false)}>
              Sulje
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Heading>
        <div className="portalNewTicketHeading">Uusi palvelupyyntö</div>
      </Heading>
      <Heading as="h4" size="md">
        Reklamaatio kuljetuspalveluista
      </Heading>
      <form>
        <div style={{ display: 'flex' }}>
          <FormControl isRequired className="portalNewTicketInputGroup">
            <FormLabel htmlFor="firstName">Etunimi</FormLabel>
            <Input
              className="portalNewTicketInput"
              type="text"
              style={{ backgroundColor: 'white' }}
              id="firstName"
              name="firstName"
              size="md"
              height="40px"
              placeholder="etunimi"
              defaultValue={props.customerData['firstName'] || ''}
              onChange={handleInputChange}
            />
          </FormControl>
          <div style={{ width: '20px' }}></div>
          <FormControl isRequired className="portalNewTicketInputGroup">
            <FormLabel htmlFor="lastName">Sukunimi</FormLabel>
            <Input
              className="portalNewTicketInput"
              type="text"
              style={{ backgroundColor: 'white' }}
              id="lastName"
              name="lastName"
              size="md"
              height="40px"
              placeholder="sukunimi"
              defaultValue={props.customerData['lastName'] || ''}
              onChange={handleInputChange}
            />
          </FormControl>
        </div>
        <div style={{ display: 'flex' }}>
          <FormControl isRequired className="portalNewTicketInputGroup">
            <FormLabel htmlFor="firstName">Sähköposti</FormLabel>
            <Input
              className="portalNewTicketInput"
              type="email"
              style={{ backgroundColor: 'white' }}
              id="email"
              size="md"
              name="email"
              height="40px"
              placeholder="sähköposti"
              defaultValue={props.customerData['email'] || ''}
              onChange={handleInputChange}
            />
          </FormControl>
          <div style={{ width: '20px' }}></div>
          <FormControl isRequired className="portalNewTicketInputGroup">
            <FormLabel htmlFor="lastName">Puhelinnumero</FormLabel>
            <Input
              className="portalNewTicketInput"
              type="phone"
              required
              style={{ backgroundColor: 'white' }}
              id="phone"
              name="phone"
              size="md"
              height="40px"
              placeholder="puhelinnumero"
              defaultValue={props.customerData['phone'] || ''}
              onChange={handleInputChange}
            />
          </FormControl>
        </div>
        <hr style={{ marginTop: '20px', marginBottom: '20px' }} />
        <div style={{ display: 'flex' }}>
          <div style={{ width: '50%' }}>
            <FormControl isRequired className="portalNewTicketInputGroup">
              <FormLabel htmlFor="firstName">Kuljettaja</FormLabel>
              <Input
                className="portalNewTicketInput"
                type="text"
                id="target"
                size="md"
                name="target"
                height="40px"
                placeholder="kuljettaja"
                onChange={handleInputChange}
              />
            </FormControl>
            <FormControl className="portalNewTicketInputGroup">
              <FormLabel htmlFor="firstName">Tapahtuma-aika</FormLabel>
              <Input
                className="portalNewTicketInput"
                type="datetime-local"
                id="date"
                name="date"
                defaultValue={formData.date}
                onChange={handleInputChange}
              />
            </FormControl>
          </div>
          <div style={{ width: '10px' }}></div>
          <div>
            <FormControl className="portalNewTicketInputGroup">
              <FormLabel htmlFor="lastName">Tapahtumapaikka</FormLabel>
              <div style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                <MapPicker
                  onLocationChange={(location: { lat: number; lng: number }) => {
                    setFormData({
                      ...formData,
                      location: `${location.lat},${location.lng}`
                    });
                  }}
                />
              </div>
            </FormControl>
          </div>
        </div>
        <hr style={{ marginTop: '20px', marginBottom: '20px' }} />

        <FormControl className="portalNewTicketInputGroup">
          <label htmlFor="fileupload"> Valitse tiedostoja </label>
          <div>
            <input ref={fileInputRef} multiple id="fileupload" type="file" onChange={(e) => setFiles(e.target.files)} />
          </div>
        </FormControl>
        <Button onClick={handleFileInputReset} colorScheme="red" variant="outline">
          Tyhjennä liitevalinnat
        </Button>
        <FormControl isRequired className="portalNewTicketInputGroup">
          <FormLabel htmlFor="content">Viesti</FormLabel>
          <Textarea
            className="portalNewTicketInput"
            id="content"
            height={28}
            name="content"
            placeholder="Sisältö"
            onChange={handleInputChange}
          />
        </FormControl>
        <Button
          isDisabled={isSendingDisabled()}
          isLoading={isSubmitting}
          loadingText="Lähetetään"
          onClick={() => onPreview()}
          colorScheme="blue"
        >
          Tarkista tiedot
        </Button>
      </form>
    </div>
  );
};

export default NewTicketForm;
